import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { EMPTY_RICH_TEXT_DOCUMENT, } from "@diality/contracts";
import { cn } from "../lib/utils";
import { ExternalLinkIcon } from "../components/ui/external-link-icon";
/** The prose treatment a document gets when its host asks for nothing else. */
const DOCUMENT_PROSE = "[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_blockquote]:my-5 [&_blockquote]:border-l-2 [&_blockquote]:border-primary/35 [&_blockquote]:pl-4 [&_blockquote]:italic [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-2xl [&_h3]:font-bold [&_h4]:mb-2 [&_h4]:mt-5 [&_h4]:text-lg [&_h4]:font-bold [&_hr]:my-8 [&_hr]:border-border [&_li]:my-1 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-4 [&_p]:leading-7 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6";
/**
 * Structured Rich Text rendered without converting the stored document to HTML.
 *
 * `className` layers over {@link DOCUMENT_PROSE} through `cn`, so a host that
 * needs its own type scale — the Text Hero's 20px lead, the Accordion's answer
 * body — states only what differs and inherits the rest. tailwind-merge resolves
 * the arbitrary variants (`[&_p]:my-4` vs `[&_p]:my-0`) the same way it resolves
 * plain utilities, so the override wins without `!important`.
 */
export function RichTextDocumentView({ document, className, }) {
    return (_jsx("div", { "data-rich-text-document-view": true, className: cn(DOCUMENT_PROSE, className), children: renderChildren(document) }));
}
export function RichTextBlock({ data }) {
    return _jsx(RichTextDocumentView, { document: data.body });
}
function linkHref(node) {
    if (node.type !== "text")
        return undefined;
    const link = node.marks?.find((mark) => mark.type === "link");
    return link ? String(link.attrs?.href ?? "") : undefined;
}
function renderChildren(node) {
    const children = node.content ?? [];
    const rendered = [];
    for (let index = 0; index < children.length; index++) {
        const child = children[index];
        const href = linkHref(child);
        if (href === undefined) {
            rendered.push(renderNode(child, index));
            continue;
        }
        // WYSIWYG formatting splits a link into adjacent text nodes. Keep those
        // runs in one anchor so the icon appears once, after the complete label.
        const key = index;
        const label = [renderNode(child, index)];
        while (index + 1 < children.length &&
            linkHref(children[index + 1]) === href) {
            index++;
            label.push(renderNode(children[index], index));
        }
        const external = /^(?:https?:)?\/\//i.test(href);
        rendered.push(_jsxs("a", { href: href, target: external ? "_blank" : undefined, rel: external ? "noopener noreferrer" : undefined, children: [label, external && (_jsx(ExternalLinkIcon, { className: "ml-1 inline-block align-baseline" }))] }, key));
    }
    return rendered;
}
function renderNode(node, key) {
    switch (node.type) {
        case "text": {
            let content = node.text ?? "";
            for (const [index, mark] of (node.marks ?? []).entries()) {
                const markKey = `${String(key)}-mark-${index}`;
                if (mark.type === "bold") {
                    content = _jsx("strong", { children: content }, markKey);
                }
                if (mark.type === "italic") {
                    content = _jsx("em", { children: content }, markKey);
                }
                if (mark.type === "underline") {
                    content = _jsx("u", { children: content }, markKey);
                }
            }
            return _jsx(React.Fragment, { children: content }, key);
        }
        case "paragraph":
            return _jsx("p", { children: renderChildren(node) }, key);
        case "heading": {
            const level = Number(node.attrs?.level);
            if (level === 2)
                return _jsx("h2", { children: renderChildren(node) }, key);
            if (level === 3)
                return _jsx("h3", { children: renderChildren(node) }, key);
            return _jsx("h4", { children: renderChildren(node) }, key);
        }
        case "bulletList":
            return _jsx("ul", { children: renderChildren(node) }, key);
        case "orderedList":
            return _jsx("ol", { children: renderChildren(node) }, key);
        case "listItem":
            return _jsx("li", { children: renderChildren(node) }, key);
        case "blockquote":
            return _jsx("blockquote", { children: renderChildren(node) }, key);
        case "horizontalRule":
            return _jsx("hr", {}, key);
        case "hardBreak":
            return _jsx("br", {}, key);
        default:
            return null;
    }
}
export function richTextFromBlock(block, _resolve) {
    const s = block.scalars ?? {};
    return {
        body: block.documents?.body ?? EMPTY_RICH_TEXT_DOCUMENT,
        align: s.align === "center" ? "center" : "left",
    };
}
export const richTextBlockEntry = {
    Component: RichTextBlock,
    fromBlock: richTextFromBlock,
};
//# sourceMappingURL=rich-text.js.map